import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Radar, AlertOctagon, RefreshCw } from "lucide-react";

const FLAG_META = {
  DORMANT_WATCH: { label: "Watch · 30-59d", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  DORMANT_60:    { label: "60+ days",       color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" },
  DORMANT_90:    { label: "90+ days",       color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
  GHOST_SUSPECT: { label: "Ghost Suspect",  color: "text-slate-900", bg: "bg-slate-800/95", border: "border-slate-900", accent: "text-white" },
};

const ORDER = ["DORMANT_WATCH", "DORMANT_60", "DORMANT_90", "GHOST_SUSPECT"];

function relTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function DormancyWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/dormancy?flag=ALL_FLAGGED");
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!data && !loading) return null;

  const counts = data?.counts_by_flag || {};
  const total = ORDER.reduce((a, k) => a + (counts[k] || 0), 0);

  const doRecompute = async () => {
    setRefreshing(true);
    try {
      await api.post("/dormancy/recompute");
      await load();
    } catch (e) {
      console.error("Dormancy recompute failed", e);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="dormancy-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Radar className="h-4 w-4 text-red-600" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Dormancy Radar · <span className="text-slate-900 font-bold">{loading ? "…" : total}</span> flagged
          </div>
          {data?.last_computed_at && (
            <span className="text-[10px] text-slate-400 ml-2">
              updated {relTime(data.last_computed_at)}
            </span>
          )}
        </div>
        <button
          onClick={doRecompute}
          disabled={refreshing}
          className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-red-600 flex items-center gap-1 disabled:opacity-50"
          data-testid="dormancy-recompute-btn"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Computing…" : "Recompute"}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {ORDER.map((flag) => {
          const meta = FLAG_META[flag];
          const c = counts[flag] || 0;
          const dark = flag === "GHOST_SUSPECT";
          return (
            <Link
              key={flag}
              to={`/dashboard/dormancy?flag=${flag}`}
              data-testid={`dormancy-tile-${flag.toLowerCase()}`}
              className={`${meta.bg} ${meta.border} border rounded-lg p-3 hover:shadow-sm transition ${dark ? "text-white" : ""}`}
            >
              <div className="flex items-center justify-between">
                <div className={`text-[10px] uppercase tracking-wider ${dark ? "text-slate-300" : "text-slate-600"} font-semibold`}>
                  {meta.label}
                </div>
                {dark && <AlertOctagon className="h-3.5 w-3.5 text-red-400" />}
              </div>
              <div className={`mt-1 text-2xl font-bold ${dark ? "text-white" : meta.color}`} style={{ fontFamily: "Chivo" }}>
                {loading ? "…" : c}
              </div>
              <div className={`text-[10px] mt-0.5 ${dark ? "text-slate-400" : "text-slate-500"}`}>
                View list →
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
